import axios from 'axios';
import { BACKEND_URL } from '../../../config/constants';
import * as Sentry from '@sentry/browser';
import { Api } from './authApi';
import { assertNotNil } from '../../../utils/types';
import { z } from 'zod';
import { freeze } from '@reduxjs/toolkit';

/**
 * Based on SyndicateInfo in Kobol. Kobol does not provide typesafe model
 * so we need to define/validate it manually
 */
const syndicateInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  region: z.string(),
  logo: z.string(),
  gitSha: z.string(),
  analyticsConfig: z.object({
    enabled: z.boolean(),
    segment: z
      .object({
        writeKey: z.string(), // may be empty string if enabled is false
      })
      .nullish(),
  }),
});

type ISyndicateInfo = z.infer<typeof syndicateInfoSchema>;

export interface ISyndicateData {
  // used for Analytics, Sentry
  syndicateId: string;
  // used for Analytics
  syndicateRegion: string;
  // used for Intercom
  syndicateName: string;
  // userPoolId and clientId are required to generate encodedData for Cognito request
  userPoolId: string;
  clientId: string;
  logo: string;
  kobolGitSha: string;
  enableSso: boolean;
  isMigration: boolean;
  // Initialize analytics
  analyticsConfig: ISyndicateInfo['analyticsConfig'];
}

class Syndicate {
  private syndicateData: ISyndicateData | undefined;
  private authApi = new Api();
  /**
   * Prepare syndicate data, intercom config and Sentry config
   */
  public initialize = async () => {
    const url = `${BACKEND_URL}/rest/syndicate`;
    try {
      const authConfig =
        await this.authApi.auth.routesAuthConfigGetConfiguration();
      const cognitoData = authConfig.data;
      assertNotNil(cognitoData);
      const { clientId, userPoolId, status, isSSOEnabled, isMigration } =
        cognitoData;

      const syndicateRes = await axios
        .get(url)
        .then((value) => syndicateInfoSchema.parse(value.data));

      const {
        id: syndicateId,
        name: syndicateName,
        logo,
        gitSha,
        analyticsConfig,
        region,
      } = syndicateRes;
      Sentry.setTag('syndicate', syndicateId);
      if (status === 'success') {
        this.syndicateData = freeze(
          {
            syndicateName,
            syndicateId,
            logo: `/images/logos/${logo}`,
            kobolGitSha: gitSha,
            userPoolId,
            clientId,
            enableSso: isSSOEnabled,
            isMigration,
            analyticsConfig,
            syndicateRegion: region,
          },
          true,
        );
      }

      return this.syndicateData;
    } catch (error) {
      console.error('Error when fetching syndicate configs: ', error);
    }
  };

  public getData = () => {
    assertNotNil(this.syndicateData);
    return this.syndicateData;
  };
}

const SyndicateObj = new Syndicate();
export default SyndicateObj;
